// Copyright (c) StrangeBee 2024

import React, { ReactElement, useState } from 'react';
import { handleNotificationError, useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { CaseTemplateEventList } from '../CaseTemplate/eventList';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { Space } from 'antd';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { RcFile } from 'antd/lib/upload';
import { THInput } from '@Molecules/Inputs/THInput/THInput';

interface TemplateImportProps {
    mode: 'page-template' | 'case-template';
    triggerElem: ReactElement;
    onCreate: CaseTemplateEventList['onCreate'];
}

const TemplateImport = ({ mode, triggerElem, onCreate }: TemplateImportProps): ReactElement => {
    const i = useLocalMessage();

    const [file, setFile] = useState<RcFile>();
    const [json, setJSON] = useState<string>();
    const [title, setTitle] = useState<string>('');

    const beforeUpload = (file: RcFile): boolean => {
        setFile(file);

        const reader = new FileReader();

        reader.onload = ({ target }): void => {
            if (target) {
                setJSON(target.result as string);
                setTitle(JSON.parse(target.result as string)?.name || JSON.parse(target.result as string)?.title);
            }
        };

        reader.readAsText(file);

        return false;
    };

    return (
        <THDrawer
            title={i(`${mode}.import.drawer.title`)}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={!!file}
        >
            {(toggleDrawer) => (
                <THForm.Layout
                    footer={
                        <FormSubmitButton
                            onCancel={() => {
                                setFile(undefined);
                                toggleDrawer();
                            }}
                            onSubmit={() => {
                                if (file) {
                                    try {
                                        if (!json) {
                                            return;
                                        }
                                        const parsedFile = JSON.parse(json);
                                        onCreate(parsedFile);
                                        setFile(undefined);
                                        setJSON(undefined);
                                    } catch (e) {
                                        handleNotificationError(e);
                                    } finally {
                                        toggleDrawer();
                                    }
                                }
                            }}
                            disabled={!json}
                        />
                    }
                >
                    <Space direction="vertical" className="file-import__container" size="large">
                        <div className="alert-import-template">
                            <AlertImport title={i(`${mode}.import.header.title`)}>
                                <>{i(`${mode}.import.header.content`)}</>
                            </AlertImport>
                        </div>
                        <Space direction="vertical" className="file-import__upload">
                            <span className="file-import__upload-title">{i('general.import.upload.label')}</span>
                            <UploadSingleFile
                                text={i('general.import.upload.content')}
                                onChange={(data) => beforeUpload(data as RcFile)}
                                value={file}
                                accept=".json"
                            />
                            <span>{i(`${mode}.import.instruction`)}</span>

                            {json && file && (
                                <>
                                    <span className="file-import__upload-title">
                                        {i(`${mode}.import.rename.title`)}
                                    </span>
                                    <THInput
                                        onBlur={(e) => {
                                            const newTemplate =
                                                mode === 'page-template'
                                                    ? JSON.stringify({
                                                          ...JSON.parse(json as string),
                                                          title: e.target.value,
                                                      })
                                                    : JSON.stringify({
                                                          ...JSON.parse(json as string),
                                                          name: e.target.value,
                                                      });
                                            setJSON(newTemplate);
                                        }}
                                        onChange={(e) => setTitle(e.target.value)}
                                        value={title}
                                    />
                                    {mode !== 'page-template' && <span>{i(`${mode}.import.rename.label`)}</span>}
                                </>
                            )}
                        </Space>
                    </Space>
                </THForm.Layout>
            )}
        </THDrawer>
    );
};

export default TemplateImport;
